import * as THREE from "three";

/**
 * The chest stage's camera (chestStage.ts): a fixed three-quarter direction from above-front, backed off until the chest
 * — wings, horns and crystals included — fills the asked share of the view, with the view offset so the chest sits
 * where the page wants it; the shakes and the burst's push in on top. Presentation only.
 */
export class ChestCamera {
  readonly camera: THREE.PerspectiveCamera;
  /** Where the camera looks and sits before shake and dolly. */
  readonly target = new THREE.Vector3(0, 0.9, 0);
  readonly eye = new THREE.Vector3();
  private dolly = 0;
  private dollyTarget = 0;
  /** The view's distance from the chest after the last framing (world units). */
  distance = 4.6;

  /** The direction the camera looks from (normalised on use), the way the icons are rendered. */
  static readonly From = new THREE.Vector3(2.2, 3.0, 3.5);
  static readonly Fov = 34;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(ChestCamera.Fov, aspect, 0.1, 40);
    this.eye.copy(ChestCamera.From);
  }

  /** Frames `bounds` at `fill` of the view height (and fitting its width), centred at (`focusX`, `focusY`) px. */
  frame(bounds: THREE.Box3, width: number, height: number, focusY: number, fill: number, focusX = width / 2): void {
    const size = bounds.getSize(new THREE.Vector3()), centre = bounds.getCenter(new THREE.Vector3());
    const tan = Math.tan(THREE.MathUtils.degToRad(ChestCamera.Fov / 2)), aspect = width / height;
    this.distance = Math.max(size.y / (fill * 2 * tan), (Math.max(size.x, size.z) * 1.2) / (2 * tan * aspect));
    this.target.copy(centre).add(new THREE.Vector3(0, size.y * 0.12, 0));   // a little room above for the lid to open
    this.eye.copy(ChestCamera.From).normalize().multiplyScalar(this.distance).add(this.target);
    this.camera.aspect = aspect;
    this.camera.setViewOffset(width, height, width / 2 - focusX, height / 2 - focusY, width, height);
    this.camera.position.copy(this.eye);
    this.camera.lookAt(this.target);
    this.camera.updateProjectionMatrix();
  }

  /** The burst's push toward the chest (world units), eased in over the next frames. */
  push(by: number): void { this.dollyTarget = by; }

  /** From `t` (the stage clock's real seconds) the camera starts its slow swing round the open chest. */
  orbitFrom(t: number): void { this.orbitT0 = t; }
  private orbitT0 = -1;
  /** The swing's reach either side (rad) and its speed. */
  static readonly OrbitReach = 0.32;
  static readonly OrbitSpeed = 0.42;

  /**
   * Per frame (`now`: the stage clock's real seconds): the swing round the chest once it is open, `shake` (the VFX
   * library's rank-scaled shake vector) plus `trauma` 0..1 wobble, then the burst's push in.
   */
  update(dt: number, now: number, shake: THREE.Vector3 | undefined, trauma: number): void {
    this.dolly += (this.dollyTarget - this.dolly) * Math.min(1, dt * 6);
    let yaw = 0;
    if (this.orbitT0 >= 0) {
      const s = now - this.orbitT0;
      yaw = ChestCamera.OrbitReach * (1 - Math.exp(-s / 1.2)) * Math.sin(s * ChestCamera.OrbitSpeed);
    }
    _eye.copy(this.eye).sub(this.target).applyAxisAngle(_y, yaw).add(this.target);
    this.camera.position.copy(_eye);
    if (this.dolly > 0.001) this.camera.position.addScaledVector(_dir.copy(this.target).sub(_eye).normalize(), this.dolly);
    this.camera.lookAt(this.target);
    const t = trauma * trauma * 0.06;
    this.camera.position.x += (shake?.x ?? 0) + Math.sin(now * 90) * t;
    this.camera.position.y += (shake?.y ?? 0) + Math.cos(now * 110) * t;
  }
}

const _dir = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _y = new THREE.Vector3(0, 1, 0);
